/**
 * Focus 专注页：FocusTimer 计时器 + 专注历史列表。
 * FocusSession：taskId/startedAt/endedAt/durationMinutes/note。
 */
import { useState } from 'react';
import { FocusTimer } from '../components/FocusTimer';
import { useFocusSessions, useTasks } from '../hooks/useTasks';
import { Card, Spinner, Pagination, EmptyState, Badge, errMsg } from '../components/ui';
import { Icon } from '../components/icons';
import type { FocusSession } from '../lib/types';

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.getMonth() + 1}月${d.getDate()}日 ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function FocusPage() {
  const [page, setPage] = useState(1);
  const { data: sessions, isLoading, error, refetch } = useFocusSessions({ page, pageSize: 20 });
  const { data: tasks } = useTasks({ pageSize: 100, includeDone: 'false' });
  const taskName = (id: string | null) =>
    id ? (tasks?.items ?? []).find((t) => t.id === id)?.title ?? '关联任务' : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">专注</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          番茄钟式专注计时，自动记录专注时长
        </p>
      </div>

      <FocusTimer onFinished={() => refetch()} />

      <div>
        <h2 className="mb-3 text-lg font-semibold">专注历史</h2>
        {isLoading && <div className="flex justify-center py-8"><Spinner className="h-8 w-8" /></div>}
        {error && <Card><p className="text-sm text-red-600 dark:text-red-400">{errMsg(error)}</p></Card>}
        {!isLoading && !error && (sessions?.items?.length ?? 0) === 0 && (
          <EmptyState
            icon={<Icon name="focus" className="mx-auto h-10 w-10" />}
            title="暂无专注记录"
            description="开始一次专注计时后将出现在这里"
          />
        )}
        <div className="space-y-2">
          {(sessions?.items ?? []).map((s: FocusSession) => (
            <Card key={s.id} className="flex flex-wrap items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                  {taskName(s.taskId) ?? '无关联任务'}
                </p>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {fmtDateTime(s.startedAt)}
                  {s.endedAt ? ` — ${fmtDateTime(s.endedAt)}` : ' — 进行中'}
                </p>
                {s.note && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{s.note}</p>}
              </div>
              <Badge
                className={s.endedAt
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
                  : 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300'}
              >
                {s.durationMinutes != null ? `${s.durationMinutes} 分钟` : '进行中'}
              </Badge>
            </Card>
          ))}
        </div>
        {sessions && sessions.totalPages > 1 && (
          <Pagination page={page} totalPages={sessions.totalPages} onPage={setPage} />
        )}
      </div>
    </div>
  );
}
